import { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ExternalLink, Megaphone, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { usePublicSettings } from '@/hooks';

const BANNER_STORAGE_KEY = 'yim_banner_dismissed';

export const BANNER_BAR_CLASS = 'bg-gradient-to-r from-[#1E40AF] to-[#2563EB] text-white text-sm';

function hashMessage(msg: string): string {
  let hash = 0;
  for (let i = 0; i < msg.length; i++) {
    hash = ((hash << 5) - hash + msg.charCodeAt(i)) | 0;
  }
  return String(Math.abs(hash));
}

interface BannerCardProps {
  title: string;
  message: string;
  linkText?: string;
  linkUrl?: string;
  onDismiss?: () => void;
  closeLabel?: string;
}

export function BannerCard({ title, message, linkText, linkUrl, onDismiss, closeLabel }: BannerCardProps) {
  const isExternal = linkUrl?.startsWith('http');

  return (
    <div className="bg-white rounded-2xl shadow-2xl shadow-slate-900/15 border border-slate-200 overflow-hidden">
      <div className={`${BANNER_BAR_CLASS} px-3 py-2 sm:px-4 flex items-center justify-between`}>
        <div className="flex items-center gap-2 text-white">
          <Megaphone className="w-4 h-4 flex-shrink-0" />
          <span className="text-xs font-semibold uppercase tracking-wider">{title}</span>
        </div>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="p-1 rounded-full hover:bg-white/20 text-white transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/80"
            aria-label={closeLabel}
          >
            <X className="w-4 h-4 sm:w-3.5 sm:h-3.5" />
          </button>
        )}
      </div>
      <div className="px-3 py-3 sm:px-4">
        <p className="text-sm text-slate-700 leading-relaxed">{message}</p>
        {linkText && linkUrl && (
          <a
            href={linkUrl}
            {...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
            className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-[#1E40AF] hover:text-[#1e3a8a] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1E40AF]/50 rounded"
          >
            {linkText}
            {isExternal ? <ExternalLink className="w-3 h-3" /> : <ArrowRight className="w-3 h-3" />}
          </a>
        )}
      </div>
    </div>
  );
}

export function SiteBanner() {
  const { i18n, t } = useTranslation();
  const { data: settings } = usePublicSettings();

  const isEnabled = settings?.BANNER_ENABLED === 'true';
  const message = i18n.language === 'fr'
    ? settings?.BANNER_MESSAGE_FR
    : settings?.BANNER_MESSAGE_EN;
  const linkText = i18n.language === 'fr'
    ? settings?.BANNER_LINK_TEXT_FR
    : settings?.BANNER_LINK_TEXT_EN;
  const linkUrl = settings?.BANNER_LINK_URL;

  const dismissKey = useMemo(
    () => `${BANNER_STORAGE_KEY}_${hashMessage(message || '')}`,
    [message],
  );

  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(dismissKey) === '1',
  );

  useEffect(() => {
    setDismissed(localStorage.getItem(dismissKey) === '1');
  }, [dismissKey]);

  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isEnabled || !message || dismissed) return;
    const timer = setTimeout(() => setVisible(true), 2000);
    return () => clearTimeout(timer);
  }, [isEnabled, message, dismissed]);

  if (!isEnabled || !message || dismissed) return null;

  const handleDismiss = () => {
    localStorage.setItem(dismissKey, '1');
    setDismissed(true);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          role="status"
          aria-live="polite"
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="fixed bottom-3 left-3 right-3 z-50 sm:right-auto sm:left-6 sm:bottom-6 sm:max-w-sm print:hidden"
        >
          <BannerCard
            title={t('admin.settings.banner.title')}
            message={message}
            linkText={linkText}
            linkUrl={linkUrl}
            onDismiss={handleDismiss}
            closeLabel={t('common.close')}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default SiteBanner;
